import React from "react";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Coins, Zap, Gift, AlertTriangle, Calendar } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useAvailableCredits } from "@/hooks/useAvailableCredits";

const getTierAllocation = (tier) => {
  const allocations = {
    free: 0,
    starter: 500,
    creator: 2000,
    professional: 5000,
    business: 15000,
    enterprise: 50000
  };
  return allocations[tier] || 0;
};

export default function CreditDisplay({ user, variant = "sidebar" }) {
  // For org members credits live in the shared org pool (personal pool = 0),
  // so we resolve the spendable balance via useAvailableCredits and show that
  // instead. Solo users fall through to their identical personal-pool values.
  const { monthly: availMonthly, topup: availTopup, total: availTotal } =
    useAvailableCredits();

  if (!user) return null;

  const monthlyCredits = availMonthly;
  const topupCredits = availTopup;

  const monthlyAllocation = getTierAllocation(user.tier);
  const totalCredits = availTotal;
  const isLowBalance = totalCredits <= (user.low_credit_threshold || 50);
  // Clamp to [0, allocation]: enterprise/custom accounts can hold MORE monthly
  // credits than the tier's nominal allocation, which made "used" go negative
  // (e.g. -444,549 / 50,000) and the progress bar invalid.
  const monthlyUsed = monthlyAllocation > 0
    ? Math.min(monthlyAllocation, Math.max(0, monthlyAllocation - monthlyCredits))
    : 0;
  const monthlyProgress = monthlyAllocation > 0
    ? Math.min(100, Math.max(0, (monthlyUsed / monthlyAllocation) * 100))
    : 0;

  if (variant === "header") {
    return (
      <div className="flex items-center gap-3">
        {isLowBalance && totalCredits > 0 && (
          <div className="flex items-center gap-1 text-orange-300">
            <AlertTriangle className="w-4 h-4" />
            <span className="text-sm font-semibold">Low Balance</span>
          </div>
        )}
        <Badge className="bg-gradient-to-r from-cyan-500 to-blue-500 text-white font-bold px-4 py-2">
          <Coins className="w-4 h-4 mr-2 text-yellow-400" />
          {totalCredits.toLocaleString()} Credits
        </Badge>
      </div>
    );
  }

  if (variant === "detailed") {
    return (
      <Card className="glass border-white/30">
        <CardHeader>
          <CardTitle className="text-white flex items-center justify-between">
            <span>Credit Balance</span>
            <div className="text-2xl font-bold text-white">{totalCredits.toLocaleString()}</div>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Monthly Credits Progress */}
          {monthlyAllocation > 0 && (
            <div>
              <div className="flex justify-between text-sm mb-2">
                <span className="text-gray-200 flex items-center gap-1 font-medium">
                  <Calendar className="w-4 h-4 text-blue-400" />
                  Monthly Credits
                </span>
                <span className="text-white font-bold">
                  {monthlyCredits} / {monthlyAllocation.toLocaleString()} remaining
                </span>
              </div>
              <Progress 
                value={100 - monthlyProgress} 
                className="h-3 bg-black/20"
              />
              <div className="flex justify-between text-xs text-gray-300 mt-1 font-medium">
                <span>Used: {monthlyUsed.toLocaleString()}</span>
                <span>Resets: {user.billing_date ? new Date(user.billing_date).toLocaleDateString() : 'N/A'}</span>
              </div>
            </div>
          )}

          {/* Top-up Credits */}
          <div>
            <div className="flex justify-between text-sm mb-2">
              <span className="text-gray-200 flex items-center gap-1 font-medium">
                <Gift className="w-4 h-4 text-cyan-400" />
                Top-up Credits
              </span>
              <span className="text-white font-bold">{topupCredits.toLocaleString()}</span>
            </div>
            <div className="h-3 bg-black/20 rounded-full">
              <div
                className="h-full bg-gradient-to-r from-cyan-400 to-pink-400 rounded-full transition-all duration-500"
                style={{width: topupCredits > 0 ? "100%" : "0%"}}
              />
            </div>
            <p className="text-xs text-gray-300 mt-1 font-medium">Never expire • Used after monthly credits</p>
          </div>

          {/* Credit Usage Priority */}
          <div className="p-3 bg-blue-500/20 border border-blue-500/30 rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <Zap className="w-4 h-4 text-blue-300" />
              <span className="text-sm font-semibold text-blue-200">Usage Priority</span>
            </div>
            <div className="text-xs text-blue-200 space-y-1 font-medium">
              <div>1. Monthly credits used first</div>
              <div>2. Top-up credits used second</div>
            </div>
          </div>

          {/* Low Balance Warning */}
          {isLowBalance && totalCredits > 0 && (
            <div className="flex items-center gap-2 p-3 bg-orange-500/20 border border-orange-500/30 rounded-lg">
              <AlertTriangle className="w-5 h-5 text-orange-300 flex-shrink-0" />
              <div>
                <p className="text-sm font-semibold text-orange-200">Low Balance Warning</p>
                <p className="text-xs text-orange-300 font-medium">
                  You have {totalCredits} credits remaining. Consider purchasing more to continue creating.
                </p>
              </div>
            </div>
          )}

          {/* Out of Credits */}
          {totalCredits === 0 && (
            <div className="flex items-center gap-2 p-3 bg-red-500/20 border border-red-500/30 rounded-lg">
              <AlertTriangle className="w-5 h-5 text-red-300 flex-shrink-0" />
              <div>
                <p className="text-sm font-semibold text-red-200">Out of Credits</p>
                <p className="text-xs text-red-300 font-medium">
                  Purchase credits to continue using NimbleNode's AI features.
                </p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  // Default sidebar variant
  return (
    <div className="bg-black/30 backdrop-blur-sm border border-white/20 rounded-lg p-3">
      <div className="flex items-center justify-between text-sm mb-3">
        <span className="text-gray-200 font-medium">Total Credits</span>
        <span className="font-bold text-white">{totalCredits.toLocaleString()}</span>
      </div>
      
      <div className="space-y-2">
        {monthlyAllocation > 0 && (
          <div className="flex justify-between text-xs text-gray-300 font-medium">
            <span>Monthly</span>
            <span>{monthlyCredits.toLocaleString()}</span>
          </div>
        )}
        <div className="flex justify-between text-xs text-gray-300 font-medium">
          <span>Top-up</span>
          <span>{topupCredits.toLocaleString()}</span>
        </div>
        
        {monthlyAllocation > 0 && (
          <div className="mt-2">
            <Progress 
              value={100 - monthlyProgress} 
              className="h-1 bg-white/10"
            />
            <p className="text-xs text-gray-300 mt-1 font-medium">
              {monthlyUsed.toLocaleString()} / {monthlyAllocation.toLocaleString()} used this month
            </p>
          </div>
        )}
      </div>

      {isLowBalance && totalCredits > 0 && (
        <div className="mt-2 flex items-center gap-1 text-orange-300">
          <AlertTriangle className="w-3 h-3" />
          <span className="text-xs font-semibold">Low balance</span>
        </div>
      )}

      {totalCredits === 0 && (
        <div className="mt-2 flex items-center gap-1 text-red-300">
          <AlertTriangle className="w-3 h-3" />
          <span className="text-xs font-semibold">Out of credits</span>
        </div>
      )}
    </div>
  );
}